/**
 * src/shell/portal/components/ContextualAdBanner.tsx
 *
 * Opt-In Contextual Attention Banner & Attention Dividend Economy.
 * - Contextual sponsor rotation based strictly on channel tags (zero tracking cookies).
 * - "Earn by Watching" attention dividend: 60% viewer, 25% channel owner, 15% scam insurance pool.
 * - Non-intrusive high-contrast design.
 */

import React, { useState } from 'react';
import { Sparkles, ExternalLink, Play, CheckCircle2, X } from 'lucide-react';
import { HumanPulseCanvas } from './HumanPulseCanvas';
import { ClassificationReport } from '../../../domain/attention/human-pulse-classifier';

export interface SponsorAd {
  id: string;
  sponsorName: string;
  headline: string;
  tagline: string;
  category: string;
  rewardTokens: number;
  clickUrl: string;
  logoEmoji: string;
}

const SPONSOR_CAMPAIGNS: SponsorAd[] = [
  {
    id: 'ad_cloud_01',
    sponsorName: 'Sovereign Edge Relay',
    headline: 'Deploy a Mesh Relay Node in Under 60 Seconds',
    tagline: 'Earn 15 tokens daily by routing encrypted P2P traffic on Raspberry Pi or ESP32.',
    category: 'tech-depin',
    rewardTokens: 3,
    clickUrl: 'https://herx.org/#/deploy-node',
    logoEmoji: '⚡',
  },
  {
    id: 'ad_synth_02',
    sponsorName: 'Kuala Lumpur Sound Labs',
    headline: 'Analog Synthesizer Presets & Acoustic Impulse Responses',
    tagline: 'Royalty-free sounds crafted on vintage Roland & Moog circuitry.',
    category: 'creators-media',
    rewardTokens: 2,
    clickUrl: 'https://herx.org/#/garage',
    logoEmoji: '🎛️',
  },
  {
    id: 'ad_hardware_03',
    sponsorName: 'YubiKey & NitroKey Sovereign Bundles',
    headline: 'Hardware FIDO2 Passkeys with Hardware PIN Attestation',
    tagline: 'Zero passwords, zero phishing vulnerabilities. Official mesh partner.',
    category: 'tech-depin',
    rewardTokens: 4,
    clickUrl: 'https://herx.org/#/store',
    logoEmoji: '🔑',
  },
];

export interface ContextualAdBannerProps {
  channelCategory?: string;
  onRewardClaimed?: (tokens: number) => void;
}

export const ContextualAdBanner: React.FC<ContextualAdBannerProps> = ({
  channelCategory = 'all',
  onRewardClaimed,
}) => {
  const [activeAdIndex, setActiveAdIndex] = useState<number>(0);
  const [isPulseModalOpen, setIsPulseModalOpen] = useState<boolean>(false);
  const [rewardClaimed, setRewardClaimed] = useState<boolean>(false);

  const matchedAds = SPONSOR_CAMPAIGNS.filter(
    (ad) => channelCategory === 'all' || ad.category === channelCategory || ad.category === 'tech-depin'
  );

  const currentAd = matchedAds[activeAdIndex % matchedAds.length] || SPONSOR_CAMPAIGNS[0];

  const handlePulseVerified = (report: ClassificationReport) => {
    setIsPulseModalOpen(false);
    setRewardClaimed(true);
    if (onRewardClaimed) {
      onRewardClaimed(currentAd.rewardTokens);
    }
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(12, 16, 26, 0.95) 100%)',
        border: '1px solid #1e293b',
        borderRadius: 14,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        color: '#f8fafc',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: '#162032',
            border: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            flexShrink: 0,
          }}
        >
          {currentAd.logoEmoji}
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: '#f59e0b', textTransform: 'uppercase' }}>
              SPONSORED SPOTLIGHT
            </span>
            <span style={{ color: '#64748b', fontSize: 11 }}>&bull; {currentAd.sponsorName}</span>
          </div>

          <div style={{ fontWeight: 700, fontSize: 13, color: '#f8fafc', marginTop: 2 }}>
            {currentAd.headline}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
            {currentAd.tagline}
          </div>
        </div>
      </div>

      {/* Action / Dividend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {!rewardClaimed ? (
          <button
            type="button"
            onClick={() => setIsPulseModalOpen(true)}
            style={{
              background: 'rgba(56, 189, 248, 0.1)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              borderRadius: 8,
              padding: '7px 12px',
              color: '#38bdf8',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Play size={13} fill="#38bdf8" />
            Earn {currentAd.rewardTokens} Tokens
          </button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#34d399', fontSize: 12, fontWeight: 700 }}>
            <CheckCircle2 size={14} /> +{currentAd.rewardTokens} Tokens Paid!
          </div>
        )}

        <a
          href={currentAd.clickUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            color: '#64748b',
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
          }}
        >
          <ExternalLink size={16} />
        </a>
      </div>

      {/* Human Pulse Modal */}
      {isPulseModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsPulseModalOpen(false);
          }}
        >
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setIsPulseModalOpen(false)}
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '50%',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#94a3b8',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <X size={16} />
            </button>
            <HumanPulseCanvas
              rewardAmountTokens={currentAd.rewardTokens}
              onVerified={handlePulseVerified}
              onCancel={() => setIsPulseModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
